import React, { useState, useContext, useCallback, useRef } from 'react';
import { Box, TextField, IconButton, Paper, Typography, CircularProgress } from '@mui/material';
import {
  Send as SendIcon,
  EmojiEmotions as EmojiIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { ChatContext } from '../../contexts/ChatContext';
import EmojiPickerPopover from './EmojiPickerPopover';

const ChatInput = ({ recipientId, conversationId }) => {
  const [message, setMessage] = useState('');
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const textFieldRef = useRef(null);
  const fileInputRef = useRef(null);
  const { sendMessage, sendTyping, uploadFile } = useContext(ChatContext);

  // Allowed file types
  const ALLOWED_EXTENSIONS = ['txt', 'pdf', 'png', 'jpg', 'jpeg', 'gif', 'doc', 'docx', 'xls', 'xlsx', 'zip', 'csv', 'mp3', 'mp4', 'webm', 'wav'];
  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

  // Check if file is an image
  const isImageFile = (file) => {
    return file && file.type.startsWith('image/');
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Debounced typing indicator
  const handleTyping = useCallback(() => {
    if (recipientId && sendTyping) {
      sendTyping(recipientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientId]); // sendTyping intentionally omitted to prevent infinite loop

  const handleChange = (e) => {
    setMessage(e.target.value);
    handleTyping();
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadError(null);

    // Validate file extension
    const extension = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      setUploadError(`Dateityp nicht erlaubt. Erlaubte Typen: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(`Datei zu groß. Maximale Größe: ${formatFileSize(MAX_FILE_SIZE)}`);
      return;
    }

    setSelectedFile(file);

    // Create preview for images
    if (isImageFile(file)) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Open file dialog
  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleSend = async () => {
    if ((!message.trim() && !selectedFile) || !recipientId) return;

    // If there's a file, upload it first
    if (selectedFile) {
      setIsUploading(true);
      setUploadError(null);

      try {
        const uploadedFile = await uploadFile(selectedFile);

        // Send message with attachment
        sendMessage(recipientId, message.trim(), uploadedFile);

        // Clear state
        setMessage('');
        handleRemoveFile();
      } catch (error) {
        console.error('Upload failed:', error);
        setUploadError(error.response?.data?.error || 'Upload fehlgeschlagen');
      } finally {
        setIsUploading(false);
      }
    } else {
      // Send text-only message
      sendMessage(recipientId, message.trim());
      setMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEmojiClick = (event) => {
    setEmojiAnchorEl(event.currentTarget);
  };

  const handleEmojiClose = () => {
    setEmojiAnchorEl(null);
  };

  const handleEmojiSelect = (emojiObject) => {
    // Insert emoji at cursor position
    const textarea = textFieldRef.current?.querySelector('textarea');
    if (textarea) {
      const cursorPos = textarea.selectionStart;
      const textBefore = message.substring(0, cursorPos);
      const textAfter = message.substring(cursorPos);
      const newMessage = textBefore + emojiObject.emoji + textAfter;
      setMessage(newMessage);

      // Set cursor position after emoji
      setTimeout(() => {
        const newCursorPos = cursorPos + emojiObject.emoji.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
        textarea.focus();
      }, 0);
    } else {
      // Fallback: append to end
      setMessage(prev => prev + emojiObject.emoji);
    }
    handleEmojiClose();
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider'
        }}
      >
        {/* File Preview */}
        {selectedFile && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              backgroundColor: 'grey.100',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            {/* Image Preview */}
            {filePreview ? (
              <Box
                component="img"
                src={filePreview}
                alt="Preview"
                sx={{
                  width: 60,
                  height: 60,
                  objectFit: 'cover',
                  borderRadius: 1
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  backgroundColor: 'grey.300',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FileIcon sx={{ color: 'grey.600' }} />
              </Box>
            )}

            {/* File Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {selectedFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(selectedFile.size)}
              </Typography>
            </Box>

            {/* Remove Button */}
            <IconButton
              size="small"
              onClick={handleRemoveFile}
              disabled={isUploading}
              sx={{ color: 'grey.500' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Upload Error */}
        {uploadError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: 'block', mb: 1 }}
          >
            {uploadError}
          </Typography>
        )}

        {/* Input Row */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          {/* Hidden File Input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept={ALLOWED_EXTENSIONS.map(ext => `.${ext}`).join(',')}
          />

          {/* Attach Button */}
          <IconButton
            onClick={handleAttachClick}
            size="small"
            disabled={isUploading}
            sx={{ color: 'text.secondary' }}
          >
            <AttachFileIcon />
          </IconButton>

          <IconButton
            onClick={handleEmojiClick}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <EmojiIcon />
          </IconButton>

          <TextField
            ref={textFieldRef}
            fullWidth
            multiline
            maxRows={4}
            value={message}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            placeholder="Nachricht eingeben..."
            variant="outlined"
            size="small"
            disabled={isUploading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />

          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={(!message.trim() && !selectedFile) || isUploading}
            sx={{
              backgroundColor: (message.trim() || selectedFile) && !isUploading ? 'primary.main' : 'action.disabledBackground',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark'
              },
              '&.Mui-disabled': {
                backgroundColor: 'action.disabledBackground',
                color: 'action.disabled'
              }
            }}
          >
            {isUploading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <SendIcon />
            )}
          </IconButton>
        </Box>
      </Paper>

      <EmojiPickerPopover
        anchorEl={emojiAnchorEl}
        open={Boolean(emojiAnchorEl)}
        onClose={handleEmojiClose}
        onEmojiClick={handleEmojiSelect}
      />
    </>
  );
};

export default ChatInput;
